import { inject, Injectable } from '@angular/core';
import { firstValueFrom, Observable, throwError, timeout, TimeoutError } from 'rxjs';
import { LoginResponse, RefreshTokenResponse } from '../model/user.model';
import { environment } from '../environments/environment';
import { HttpService } from './http.service';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';

const REFRESH_TIMEOUT_MS = 8000;

@Injectable({ providedIn: 'root' })
export class AuthService {
  private httpService = inject(HttpService);

  // In-flight refresh observable shared across parallel callers.
  // Null when no refresh is running. A single shared observable (via
  // shareReplay) guarantees every caller — first or late — sees the same
  // emission/error/completion, which prevents waiter starvation when
  // refresh fails.
  private refreshInFlight$: Observable<RefreshTokenResponse> | null = null;

  // getCurrentUser(): Observable<User> {
  //   return this.httpService
  //     .get<User>(`${environment.endPoint}/user/users`, new HttpParams(), true)
  //     .pipe(
  //       tap( user => console.log(`User ${user}`)),
  //       timeout(10000), // 10 second timeout (same as logout)
  //       catchError((error) => {
  //         if (error.name === 'TimeoutError') {
  //           console.log('Auth check timed out');
  //         }
  //         return throwError(() => error);
  //       }),
  //     );
  // }

  /**
   * Logout user and revoke refresh token on server
   * Note: Even if server call fails, we clear local state for security
   * Includes 5-second timeout to prevent hanging on network issues
   */
  logout(): Observable<void> {
    // The refresh token rides in the HttpOnly cookie (withCredentials: true); the backend revokes
    // it and clears the cookie. The access token is added by the auth interceptor. On error the
    // effect still clears local state (logoutSuccess), and a 401 is turned into forceLogout by the
    // interceptor.
    return this.httpService
      .post<{ message: string }>(`${environment.endPoint}/auth/logout`, {}, true)
      .pipe(
        timeout(5000), // 5 second timeout for logout
        map(() => {
          console.log('Refresh token revoked on server');
        }),
        catchError((error) => {
          if (error instanceof TimeoutError) {
            console.error('Logout request timed out after 5 seconds');
          } else {
            console.error('Logout API call failed', error);
          }
          return throwError(() => error);
        }),
      );
  }

  /**
   * Login with WebAuthn passkey
   * Uses /webauthn-login endpoint which returns both access and refresh tokens
   */
  loginWithPasskey(credentials: {
    credentialId: string;
    clientDataJSON: string;
    authenticatorData: string;
    signature: string;
    clientExtensions: string;
  }): Promise<LoginResponse> {
    return firstValueFrom(
      this.httpService.post<LoginResponse>(
        `${environment.endPoint}/webauthn-login`,
        credentials,
        true,
      ),
    );
  }

  /**
   * Refresh access token using refresh token.
   *
   * Parallel callers share a single in-flight refresh. Errors and completion
   * propagate to every subscriber (no waiter starvation). Bounded by an
   * 8s timeout so a stalled network never hangs the UI forever.
   */
  refreshAccessToken(): Observable<RefreshTokenResponse> {
    if (this.refreshInFlight$) {
      console.log('Token refresh already in progress, reusing in-flight request');
      return this.refreshInFlight$;
    }

    console.log('Starting token refresh...');

    // The refresh token is sent automatically as the HttpOnly cookie (withCredentials: true); no
    // token is placed in the request body. Bounded by an 8s timeout so a stalled network never
    // hangs the UI. Concurrent in-tab callers share this one in-flight request via shareReplay;
    // cross-tab collisions are handled on the backend by the rotation grace window (no client-side
    // cross-tab lock — an earlier Web Locks attempt deadlocked the blocking boot initializer).
    this.refreshInFlight$ = this.httpService
      .post<RefreshTokenResponse>(`${environment.endPoint}/auth/refresh`, {}, true)
      .pipe(
        timeout(REFRESH_TIMEOUT_MS),
        tap(() => console.log('Token refresh completed successfully')),
        catchError((error) => {
          if (error instanceof TimeoutError) {
            console.error(`Token refresh timed out after ${REFRESH_TIMEOUT_MS}ms`);
          } else {
            console.error('Token refresh failed', error);
          }
          return throwError(() => error);
        }),
        finalize(() => {
          this.refreshInFlight$ = null;
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );

    return this.refreshInFlight$;
  }

  registerWithPasskey(data: {
    userHandle: string;
    username: string;
    clientDataJSON: string;
    attestationObject: string;
    transports: string[];
    clientExtensions: string;
  }): Promise<{ message?: string; error?: string }> {
    return firstValueFrom(
      this.httpService.post<{ message?: string; error?: string }>(
        `${environment.endPoint}/signup`,
        data,
        true,
      ),
    );
  }
}
